import { useTranslation } from "react-i18next";
import { useContext } from "react";
import moment from "moment";
import { SuperOrder } from "@beeldit/ecommerce/super-orders/models/SuperOrder";
import OrderDetails from "@beeldit/ecommerce/orders/components/OrderDetails";
import React from "react";
import useChangeStatusAction from "@beeldit/ecommerce/orders/actions/change-status/useChangeStatusAction";
import useDownloadShipmentLabelAction from "@beeldit/ecommerce/orders/actions/download-shipment-label/useDownloadShipmentLabelAction";
import { Link } from "react-router-dom";
import { ListGroup } from "react-bootstrap";
import useUploadInvoiceAction from "@beeldit/ecommerce/orders/actions/upload-invoice/useUploadInvoiceAction";
import useDownloadInvoiceAction from "@beeldit/ecommerce/orders/actions/download-invoice/useDownloadInvoiceAction";

interface Prop {
  context: any;
  getFunction: any;
}

interface Context {
  element: SuperOrder;
  setElement: (element: any) => any;
  backendFormErrors: any;
}

function SuperOrderDetails(props: Prop) {
  const { context, getFunction } = props;
  const { element } = useContext<Context>(context);

  const { t } = useTranslation();

  const onSuccess = () => {
    getFunction(element.id);
  };
  const { prepareChangeStatusFunction, actionTemplate } =
    useChangeStatusAction(onSuccess);
  const { downloadShipmentLabelFunction } = useDownloadShipmentLabelAction();
  const {
    prepareUploadInvoiceFunction,
    actionTemplate: uploadInvoiceActionTemplate,
  } = useUploadInvoiceAction(onSuccess);

  const { downloadInvoiceFunction } = useDownloadInvoiceAction();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  const fragment =
    element?.customer.user.roles.length &&
    element?.customer.user.roles[0].name === "dealer"
      ? "dealers"
      : "retails";

  return (
    <>
      <div className="order-container">
        <div className="client-data">
          {element.customer && element.customer.user && (
            <div className="mb-4">
              <h5 className="data-label">{t("customer")}</h5>
              <ul className="list-unstyled">
                <li>
                  <Link
                    className="client-name link"
                    to={`/panel/1/1/${fragment}/${element?.customer.id}`}
                  >
                    {element?.customer.user.name}{" "}
                    {element?.customer.user.surnames}
                  </Link>
                </li>
                <li>
                  <Link
                    className="mailto"
                    to={`mailto:${element?.customer.user.email}`}
                  >
                    <small>{element?.customer.user.email}</small>
                  </Link>
                </li>
              </ul>
            </div>
          )}
          {element.shipping_address && (
            <div className="mb-4 pt-4 border-top">
              <h5 className="data-label">{t("shipping_address")}</h5>
              <p>
                {element.shipping_address.street && (
                  <span>{element.shipping_address.street}</span>
                )}
                {element.shipping_address.postal_code && (
                  <span>, {element.shipping_address.postal_code}</span>
                )}
                {element.shipping_address.city && (
                  <span> , {element.shipping_address.city}</span>
                )}
                {element.shipping_address.state && (
                  <span>, {element.shipping_address.state}</span>
                )}
                {element.shipping_address.country && (
                  <span>, {element.shipping_address.country}</span>
                )}
              </p>
            </div>
          )}
          {element.billing_address && (
            <div className="mb-4 pt-4 border-top">
              <h5 className="data-label">{t("billing_address")}</h5>
              <p>
                {element.billing_address.fiscal_address && (
                  <span>{element.billing_address.fiscal_address}</span>
                )}
                {element.billing_address.fiscal_zip && (
                  <span> , {element.billing_address.fiscal_zip}</span>
                )}
                {element.billing_address.fiscal_city && (
                  <span>, {element.billing_address.fiscal_city}</span>
                )}
                {element.billing_address.fiscal_province && (
                  <span>, {element.billing_address.fiscal_province}</span>
                )}
                {element.billing_address.fiscal_country_code && (
                  <span>, {element.billing_address.fiscal_country_code}</span>
                )}
              </p>
            </div>
          )}
        </div>

        <div className="order-meta">
          <h5 className="data-label">{t("reference")}</h5>
          <div className="client-ref mb-4">{element?.reference}</div>

          {element.created_at && (
            <>
              <h5 className="data-label">{t("created_at")}</h5>
              <div className="client-date mb-4">
                {moment(element?.created_at).format("DD/MM/YYYY HH:mm")}
              </div>
            </>
          )}

          <h5 className="data-label">{t("observations")}</h5>
          <div className="client-ref mb-4">
            <small>{element?.observations}</small>
          </div>
        </div>

        <div className="order-amount bg-info p-4">
          <ListGroup variant="flush">
            <ListGroup.Item variant="info">
              <small>{t("subtotal")}</small>
              <span>
                {formatPrice(
                  element?.total_prices.subtotal_without_discount ??
                    element?.total_prices.subtotal
                )}
              </span>
            </ListGroup.Item>

            {element?.total_prices.discount > 0 && (
              <ListGroup.Item variant="info">
                <small>{t("discount")}</small>
                <span className="text-danger ms-2">
                  - {formatPrice(element?.total_prices.discount)}
                </span>
              </ListGroup.Item>
            )}

            <ListGroup.Item variant="info">
              <small>{t("tax")}</small>
              <span>{formatPrice(element?.total_prices.tax)}</span>
            </ListGroup.Item>

            {((Number(element?.total_prices.shipping) || 0) > 0 ||
              (Number(element?.total_prices.shipping_tax) || 0) > 0) && (
              <ListGroup.Item variant="info">
                <small>{t("shipping")}</small>
                <span>
                  {formatPrice(
                    (Number(element?.total_prices.shipping) || 0) +
                      (Number(element?.total_prices.shipping_tax) || 0)
                  )}
                </span>
              </ListGroup.Item>
            )}

            <ListGroup.Item variant="info">
              <strong>{t("total")}</strong>
              <strong>{formatPrice(element?.total_prices.total)}</strong>
            </ListGroup.Item>

            {element.payment?.payment_method && (
              <ListGroup.Item variant="info">
                <small>{t("payment_method")}</small>
                <span className="ms-2">
                  {t(element.payment.payment_method.type)}
                </span>
              </ListGroup.Item>
            )}
          </ListGroup>
        </div>
      </div>

      {element.orders.map((order) => (
        <OrderDetails
          key={"order-" + order.id}
          order={order}
          prepareChangeStatusFunction={prepareChangeStatusFunction}
          downloadShipmentLabelFunction={downloadShipmentLabelFunction}
          prepareUploadInvoiceFunction={prepareUploadInvoiceFunction}
          downloadInvoiceFunction={downloadInvoiceFunction}
          superOrder={element}
        />
      ))}
      {actionTemplate}
      {uploadInvoiceActionTemplate}
    </>
  );
}

export default SuperOrderDetails;
